import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Drawer,
  List,
  Typography,
  Box,
  Divider
} from '@mui/material';
import PropTypes from 'prop-types';
import MenuItem from './MenuItem';

const DRAWER_WIDTH = 260;

/**
 * DynamicSidebar - Permission-based dynamic sidebar
 * 
 * Phase 5: M5.T5.2 - Dynamic Sidebar
 * 
 * Features:
 * - Renders menu from Redux (state.auth.menu)
 * - Hierarchical menu structure (recursive)
 * - Responsive (drawer on mobile, permanent on desktop)
 * - Active route highlighting
 * - Material-UI icons support
 * 
 * Menu Structure Example:
 * [
 *   { code: 'dashboard', name: 'Dashboard', path: '/dashboard', icon: 'DashboardIcon', children: [] },
 *   { code: 'company', name: 'Şirket Yönetimi', icon: 'BusinessIcon', children: [
 *     { code: 'users.view', name: 'Kullanıcılar', path: '/company/users', icon: 'PeopleIcon' }
 *   ]}
 * ]
 */
const DynamicSidebar = ({ open, onClose, isMobile }) => {
  const { menu, user } = useSelector((state) => state.auth);
  const location = useLocation();
  const history = useHistory();
  
  const handleNavigate = (path) => {
    history.push(path);
    if (isMobile && open) {
      onClose();
    }
  };
  
  const drawerContent = (
    <>
      {/* Logo/Brand - Clickable (goes to /dashboard) */}
      <Box 
        sx={{ 
          p: 2, 
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}
        onClick={() => handleNavigate('/dashboard')}
      >
        {/* Logo Image */}
        <Box 
          component="img"
          src="/contractcode-logo.png"
          alt="ContractCode"
          sx={{ 
            height: 64,
            width: 'auto',
            display: 'block',
            mx: 'auto',
            mb: 1
          }}
        />
        
        {/* Panel Type Label */}
        <Typography 
           variant="caption" 
          color="text.secondary"
          sx={{ 
            display: 'block',
            fontSize: '0.75rem',
            fontWeight: 500
          }}
        >
          {user?.userType === 'company_owner' ? 'Şirket Paneli' : 
           user?.userType === 'company_member' ? 'Şirket Kullanıcı Paneli' :
           user?.userType === 'system_admin' ? 'Admin Paneli' : 'Kullanıcı Paneli'}
        </Typography>
      </Box>
      
      <Divider />
      
      {/* Dynamic Menu */}
      <Box sx={{ overflow: 'auto', mt: 1 }}>
        {menu && menu.length > 0 ? (
          <List component="nav" disablePadding>
            {menu
              .slice() // Create copy to avoid mutating original
              .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order
              .map(item => (
                <MenuItem
                  key={item.code}
                  item={item}
                  currentPath={location.pathname}
                />
              ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Menü yükleniyor...
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
  
  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{
        keepMounted: true // Better mobile performance
      }}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider'
        }
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

DynamicSidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired
};

export default DynamicSidebar;

